import { useMemo, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import type { AnyExtension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import { FontSize } from '../extensions/FontSize';
import { FontFamily } from '../extensions/FontFamily';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Suggestion from '@tiptap/suggestion';
import { HandlebarsNode } from '../extensions/handlebars/HandlebarsNode';
import { HandlebarsBlock } from '../extensions/handlebars/HandlebarsBlock';
import { HandlebarsBlockStart } from '../extensions/handlebars/HandlebarsBlockStart';
import { HandlebarsBlockEnd } from '../extensions/handlebars/HandlebarsBlockEnd';
import { HandlebarsInputRules } from '../extensions/handlebars/HandlebarsInputRules';
import { createHandlebarsSuggestion } from '../extensions/handlebars/HandlebarsSuggestion';
import { TabHandler } from '../extensions/TabHandler';
import { TrailingNode } from '../extensions/TrailingNode';
import { BlockGapEnforcer } from '../extensions/BlockGapEnforcer';
import { convertToTemplate, convertFromTemplate, migrateBookmarkToWrapping } from '../utils/templateConverter';
import { extractDocumentParts, reassembleDocument, scopeStylesToContainer } from '../utils/documentWrapper';
import { resolveNodeDescriptions } from '../utils/resolveDescriptions';
import type { DocumentParts } from '../utils/documentWrapper';
import type { RichTextEditorProps } from '../types';
import { Toolbar } from './Toolbar';

import '../styles/editor.css';
import '../styles/handlebars.css';
import '../styles/autocomplete.css';

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialContent = '',
  onChange,
  onTemplateChange,
  autocompleteProvider,
  placeholder = 'Start typing...',
  className,
  toolbar,
  editable = true,
  showDescriptions = false,
  callbacks,
  colorPalette,
  highlightPalette,
  fontSizes,
  fontFamilies,
  allowFreeformHandlebars = false,
  maxHeight,
}) => {
  // Ref to store the structural document envelope (html/head/style/body tags)
  const documentPartsRef = useRef<DocumentParts>({
    beforeBody: '', bodyContent: '', afterBody: '', styles: [], isFullDocument: false,
  });
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const isResolvingRef = useRef(false);
  const resolveTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Convert initial content from template format or migrate legacy bookmark-style blocks
  const normalizedInitialContent = useMemo(() => {
    // Extract structural envelope before Tiptap can strip it
    const parts = extractDocumentParts(initialContent);
    documentPartsRef.current = parts;

    let content = parts.bodyContent;

    // Migrate old bookmark-style blocks (paired start/end spans) to wrapping divs
    if (content.includes('data-handlebars-block-start')) {
      content = migrateBookmarkToWrapping(content);
    }

    // Convert raw {{}} template syntax to editor HTML
    if (content.includes('{{') && !content.includes('data-handlebars')) {
      content = convertFromTemplate(content);
    }

    return content;
  }, [initialContent]);

  const extensions = useMemo(() => {
    const baseExtensions: AnyExtension[] = [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      // Text formatting
      Underline,
      Subscript,
      Superscript,
      TextStyle,
      Color,
      FontSize,
      FontFamily,
      Highlight.configure({
        multicolor: true,
      }),
      // Text alignment
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      // Links
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
        },
      }),
      // Images
      Image.configure({
        inline: false,
        allowBase64: true,
      }),
      // Task lists
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      // Tables
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      // Tab handling for indentation
      TabHandler,
      // Ensure document always ends with an editable paragraph
      TrailingNode,
      // Ensure editable paragraphs exist between adjacent handlebars blocks
      BlockGapEnforcer,
    ];

    // Wrapping block extension (new container approach)
    baseExtensions.push(HandlebarsBlock.configure({
      autocompleteProvider,
      showDescriptions,
      allowFreeformHandlebars,
    }));

    // Legacy block extensions for parsing old content
    baseExtensions.push(HandlebarsBlockStart);
    baseExtensions.push(HandlebarsBlockEnd);

    // Add input rules for automatic tag conversion when typing {{...}}
    baseExtensions.push(HandlebarsInputRules.configure({
      allowFreeformHandlebars,
    }));

    if (autocompleteProvider) {
      const suggestionConfig = createHandlebarsSuggestion({
        provider: autocompleteProvider,
        showDescriptions,
        allowFreeformHandlebars,
      });

      const handlebarsWithSuggestion = HandlebarsNode.configure({
        suggestion: suggestionConfig,
        autocompleteProvider,
        showDescriptions,
        allowFreeformHandlebars,
      }).extend({
        addProseMirrorPlugins() {
          const suggestionOptions = (this.options as { suggestion: Parameters<typeof Suggestion>[0] }).suggestion;
          return [
            Suggestion({
              ...suggestionOptions,
              editor: this.editor,
            }),
          ];
        },
      });
      baseExtensions.push(handlebarsWithSuggestion);
    } else {
      baseExtensions.push(HandlebarsNode);
    }

    return baseExtensions;
  }, [autocompleteProvider, placeholder, showDescriptions, allowFreeformHandlebars]);

  const editor = useEditor({
    extensions,
    content: normalizedInitialContent,
    editable,
    onUpdate: ({ editor }) => {
      if (isResolvingRef.current) return;
      const html = editor.getHTML();
      const parts = documentPartsRef.current;
      onChange?.(reassembleDocument(html, parts));
      onTemplateChange?.(reassembleDocument(convertToTemplate(html), parts));

      // Debounced re-resolve for nodes created after initial load (e.g., via input rules)
      if (showDescriptions && autocompleteProvider) {
        clearTimeout(resolveTimerRef.current);
        resolveTimerRef.current = setTimeout(async () => {
          if (editor.isDestroyed || isResolvingRef.current) return;
          isResolvingRef.current = true;
          try {
            await resolveNodeDescriptions(editor, autocompleteProvider, showDescriptions, allowFreeformHandlebars);
          } catch (err) {
            console.warn('Failed to resolve handlebars descriptions:', err);
          } finally {
            isResolvingRef.current = false;
          }
        }, 500);
      }
    },
  });

  // Resolve descriptions for handlebars nodes loaded from initialContent
  useEffect(() => {
    if (!editor || !autocompleteProvider || !showDescriptions) return;

    const timer = setTimeout(async () => {
      if (editor.isDestroyed) return;
      isResolvingRef.current = true;
      try {
        await resolveNodeDescriptions(editor, autocompleteProvider, showDescriptions, allowFreeformHandlebars);
      } catch (err) {
        console.warn('Failed to resolve handlebars descriptions:', err);
      } finally {
        isResolvingRef.current = false;
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [editor, autocompleteProvider, showDescriptions]);

  // Inject scoped <style> into the editor when the document has embedded styles
  useEffect(() => {
    const parts = documentPartsRef.current;
    if (!parts.isFullDocument || parts.styles.length === 0) return;

    const container = editorWrapperRef.current;
    if (!container) return;

    const scopeClass = 'rich-editor-doc-scoped';
    const editorContent = container.querySelector('.rich-editor-content');
    if (editorContent) {
      editorContent.classList.add(scopeClass);
    }

    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-rich-editor-scoped', 'true');
    styleEl.textContent = parts.styles
      .map(css => scopeStylesToContainer(css, `.${scopeClass}`))
      .join('\n');
    container.appendChild(styleEl);

    return () => {
      styleEl.remove();
      if (editorContent) {
        editorContent.classList.remove(scopeClass);
      }
    };
  }, [initialContent]);

  if (!editor) {
    return null;
  }

  return (
    <div
      ref={editorWrapperRef}
      className={`rich-editor-wrapper ${className || ''}`}
      style={maxHeight ? { maxHeight, display: 'flex', flexDirection: 'column' as const } : undefined}
    >
      {editable && (
        <Toolbar
          editor={editor}
          config={toolbar}
          callbacks={callbacks}
          colorPalette={colorPalette}
          highlightPalette={highlightPalette}
          fontSizes={fontSizes}
          fontFamilies={fontFamilies}
        />
      )}
      <EditorContent
        editor={editor}
        className="rich-editor-content"
        style={maxHeight ? { overflowY: 'auto' as const, flex: 1 } : undefined}
      />
    </div>
  );
};

export default RichTextEditor;
